import { useRouter } from "next/router"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useScrollContext } from "@/components/resources/context/ScrollPage/ScrollPageContext"
import type { TableOfContents as TableOfContentsType } from "@/components/resources/data/posts.types"
import { getHashPath } from "@/components/resources/utils/routerHelpers"

interface TableOfContentsProps {
  title?: string
  tableOfContents: TableOfContentsType[]
  rootMargin?: IntersectionObserverInit["rootMargin"]
  className?: string
  children?: React.ReactNode
}

interface TableOfContentsInnerProps extends TableOfContentsProps {
  rootMargin: NonNullable<IntersectionObserverInit["rootMargin"]>
}

/*
 * a) User clicks a ToC link -> Scroll to anchor and use push history
 * b) User scrolls past a heading -> Scroll spy updates highlight and URL
 */

const TableOfContents = ({ tableOfContents, children, className }: TableOfContentsInnerProps) => {
  const { asPath, replace, push, events: routerEvents } = useRouter()
  const { scrollStatus, scrollNavigate } = useScrollContext()
  const selfPathRef = useRef(getHashPath(tableOfContents[0].href).path)

  const isRoutingRef = useRef(false)
  const navRef = useRef<HTMLElement | null>(null)
  const scrollingRef = useRef(scrollStatus)
  const hashDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  // Local state for instant TOC highlighting — decoupled from async router updates.
  // Default to first heading when URL has no hash (initial page load).
  const [activeAnchor, setActiveAnchor] = useState<string | null>(() => {
    const urlHash = getHashPath(asPath).hash
    if (urlHash) return urlHash
    return tableOfContents[0] ? getHashPath(tableOfContents[0].href).hash : null
  })
  const activeAnchorRef = useRef(activeAnchor)

  // Is active, as in does this ToC belong to a page/section being observed. If single page/article, then always yes.
  const [isActive, setActive] = useState(selfPathRef.current === getHashPath(asPath).path)

  // Sync activeAnchor from external URL changes (back/forward navigation, initial load)
  useEffect(() => {
    const { path, hash } = getHashPath(asPath)
    if (path === selfPathRef.current) {
      activeAnchorRef.current = hash
      setActiveAnchor(hash)
      if (!isActive) {
        setActive(true)
      }
    } else if (isActive) {
      setActive(false)
    }
  }, [asPath, isActive])

  const replaceHashPath = useCallback(
    (hash: string | null, path = selfPathRef.current) => {
      // Debounce URL updates — visual highlighting is handled by activeAnchor state,
      // so the URL update can be slower without causing visual flickering.
      clearTimeout(hashDebounceRef.current)
      hashDebounceRef.current = setTimeout(() => {
        const url = hash ? `${path}#${hash}` : path

        replace(url, undefined, { shallow: true, scroll: false }).catch((err) => {
          console.error(err)
        })
      }, 200)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [replace],
  )

  const scrollToEl = useCallback(
    async (anchorOfEl: null | string, href: string) => {
      if (anchorOfEl) {
        const scrollTarget = document.querySelector(`[data-anchor="${anchorOfEl}"]`)

        if (scrollTarget) {
          scrollNavigate()
          // Update highlight immediately on click
          activeAnchorRef.current = anchorOfEl
          setActiveAnchor(anchorOfEl)
          return requestAnimationFrame(() => {
            window.scrollBy({
              top: scrollTarget.getBoundingClientRect().top - scrollingRef.current.offset.anchor,
              behavior: "smooth",
            })
            push(href, undefined, { shallow: true, scroll: false })
          })
        }
      }
    },
    [push, scrollNavigate],
  )

  const tableOfContentsHrefs = useMemo(() => {
    const hrefs: Array<string> = []
    tableOfContents.forEach(({ href, subHeadings }) => {
      hrefs.push(href)
      subHeadings?.forEach(({ href }) => {
        hrefs.push(href)
      })
    })

    return hrefs
  }, [tableOfContents])

  // Scroll spy: on every scroll, find the last heading above the threshold line.
  // This is deterministic — same scroll position always gives same active heading.
  useEffect(() => {
    if (!isActive) return

    // Build list of heading elements with their metadata
    const headings = tableOfContentsHrefs
      .map((href) => {
        const { hash, path } = getHashPath(href)
        const el = document.querySelector<HTMLElement>(`[data-anchor="${hash ?? ""}"]`)
        return el ? { el, hash, path } : null
      })
      .filter((h): h is NonNullable<typeof h> => h !== null)

    if (!headings.length) return

    let rafId: number | undefined

    const updateActiveHeading = () => {
      rafId = undefined
      if (scrollingRef.current.isProgrammaticallyScrolling) return

      // Find the last heading whose top is at or above the scroll threshold.
      // This naturally handles both scroll-up and scroll-down — the heading
      // you most recently scrolled past is always the active one.
      const threshold = scrollingRef.current.offset.anchor + 20
      let active: (typeof headings)[number] | null = null

      for (const heading of headings) {
        if (heading.el.getBoundingClientRect().top <= threshold) {
          active = heading
        }
      }

      // If no heading is above the threshold (we're above the first section), default to first
      if (!active) {
        active = headings[0]
      }

      if (active.hash !== activeAnchorRef.current) {
        activeAnchorRef.current = active.hash
        setActiveAnchor(active.hash)
        replaceHashPath(active.hash, active.path)
      }
    }

    const onScroll = () => {
      if (rafId !== undefined) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(updateActiveHeading)
    }

    // Set initial active heading
    updateActiveHeading()

    document.addEventListener("scroll", onScroll, { passive: true })

    // Router event handlers to prevent overlapping updates during navigation
    const handleHashChangeComplete = () => {
      isRoutingRef.current = false
    }
    const handleHashChangeStart = () => {
      isRoutingRef.current = true
      if (rafId !== undefined) cancelAnimationFrame(rafId)
    }
    routerEvents.on("routeChangeStart", handleHashChangeStart)
    routerEvents.on("routeChangeComplete", handleHashChangeComplete)
    routerEvents.on("hashChangeStart", handleHashChangeStart)
    routerEvents.on("hashChangeComplete", handleHashChangeComplete)

    return () => {
      document.removeEventListener("scroll", onScroll)
      if (rafId !== undefined) cancelAnimationFrame(rafId)
      clearTimeout(hashDebounceRef.current)
      routerEvents.off("routeChangeStart", handleHashChangeStart)
      routerEvents.off("routeChangeComplete", handleHashChangeComplete)
      routerEvents.off("hashChangeStart", handleHashChangeStart)
      routerEvents.off("hashChangeComplete", handleHashChangeComplete)
    }
  }, [tableOfContentsHrefs, replaceHashPath, routerEvents, isActive])

  useEffect(() => {
    // This use-effect only affects the navigation itself in cases where the table of contents list is so long that it ends up being scrollable
    let timeoutFrame: number | undefined
    if (navRef.current && !scrollingRef.current?.isProgrammaticallyScrolling) {
      const { scrollHeight, clientHeight } = navRef.current
      if (scrollHeight > clientHeight) {
        timeoutFrame = requestAnimationFrame(() => {
          const currentNavItem =
            navRef.current?.querySelector<HTMLAnchorElement>('a[aria-current="page"]')
          if (currentNavItem && navRef.current) {
            const top = currentNavItem.offsetTop - clientHeight / 2
            navRef.current.scroll({
              behavior: "smooth",
              top,
            })
          }
        })
      }
    }
    return () => {
      if (timeoutFrame) {
        cancelAnimationFrame(timeoutFrame)
      }
    }
  }, [])

  // Helper to check if a TOC href matches the currently active anchor
  const isActiveHref = (href: string) => {
    if (!isActive) return false
    const { hash } = getHashPath(href)
    return hash === activeAnchor
  }

  return (
    <div className={`sticky md:top-[75px] md:-mt-4 py-2 ${className || ""}`}>
      {children}
      <nav
        className="overflow-hidden overflow-x-hidden max-h-[calc(100vh-75px*2-40px)] -ml-px pl-px pt-1"
        ref={navRef}
      >
        <ul className="list-none">
          {tableOfContents.map(({ href, title, subHeadings }) => (
            <li key={href}>
              <a
                href={href}
                className={`py-2 text-sm block no-underline hover:underline ${
                  isActiveHref(href) ? "-ml-px text-fg-default" : "text-fg-secondary"
                }`}
                aria-current={isActiveHref(href) ? "page" : undefined}
                onClick={(e) => {
                  e.preventDefault()
                  if (!isActiveHref(href)) {
                    scrollToEl(getHashPath(href).hash, href)
                  }
                }}
              >
                {title}
              </a>
              {subHeadings?.length ? (
                <ul className="list-none">
                  {subHeadings.map(({ href, title }) => (
                    <li key={href}>
                      <a
                        href={href}
                        className={`text-sm py-2 block no-underline hover:underline pl-8 ${
                          isActiveHref(href) ? "-ml-px text-neutral-50" : "text-fg-secondary"
                        }`}
                        aria-current={isActiveHref(href) ? "page" : undefined}
                        onClick={(e) => {
                          e.preventDefault()
                          if (!isActiveHref(href)) {
                            scrollToEl(getHashPath(href).hash, href)
                          }
                        }}
                      >
                        {title}
                      </a>
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      </nav>
    </div>
  )
}

const TableOfContentsHeading = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => <h2 className={`font-medium py-1 ${className || ""}`}>{children}</h2>

const TableOfContentsSidebar = ({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}) => (
  <aside className={`w-full md:w-auto hidden md:block md:[grid-area:sidebar] ${className || ""}`}>
    {children}
  </aside>
)

export const TableOfContentsLayout = ({
  tableOfContents,
  children,
  className,
  title = "Content",
  rootMargin = "-100px 0px -55%",
}: TableOfContentsProps) => {
  return (
    <div
      className={`flex flex-col md:flex-row w-full md:justify-between max-w-[1064px] mx-auto ${className || ""}`}
    >
      <TableOfContentsSidebar>
        <TableOfContents tableOfContents={tableOfContents} rootMargin={rootMargin}>
          {title && <TableOfContentsHeading>{title}</TableOfContentsHeading>}
        </TableOfContents>
      </TableOfContentsSidebar>
      <div className="[grid-area:main]">{children}</div>
    </div>
  )
}
